import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { apiFetch, ApiError, setSelectedCenterId, getSelectedCenterId, clearSelectedCenterId } from "./api";
import { clearApiCache, invalidateCache } from "./useApiState";
import { clearWelcomeState } from "./welcomeSession";
import { startCenterSwitch } from "./centerTransition";
import type { Center, DataUseAcknowledgmentConfig, MeResponse, MeSubscription, TenantSettings, TwoFactorValidateSessionResponse, User, UserPermissions, UserRole } from "./types";
import { LoadingScreen } from "../components/LoadingScreen";

// Cross-domain cookie for marketing site auth detection
function setCradleosCookie(tenantName: string) {
  const domain = window.location.hostname === 'localhost' ? 'localhost' : '.cradleos.com';
  const maxAge = 30 * 24 * 60 * 60; // 30 days
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `cradleos_tenant=${encodeURIComponent(tenantName)}; domain=${domain}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
}

function clearCradleosCookie() {
  const domain = window.location.hostname === 'localhost' ? 'localhost' : '.cradleos.com';
  document.cookie = `cradleos_tenant=; domain=${domain}; path=/; max-age=0`;
}

// LocalStorage key for 2FA trusted device token
const TRUSTED_DEVICE_TOKEN_KEY = "cradleos_2fa_session";

type AuthStatus = "loading" | "anonymous" | "authenticated" | "payment_required";

// Split contexts to prevent cascading re-renders
// 1. User context - user data that rarely changes
type AuthUserContextValue = {
  user: User | null;
  tenantId: number | null;
  assignedClassroomIds: number[];
  organizationName: string | null;
  subscription: MeSubscription | null;
  tenantSettings: TenantSettings | null;
  centers: Center[];
  selectedCenterId: number | null;
  defaultCenterId: number | null;
  permissions: UserPermissions | null;
  dataUseAcknowledgment: DataUseAcknowledgmentConfig | null;
  programType: string | null;
  homeLicenseType: string | null;
};

// 2. Status context - auth status that changes on login/logout/refresh
type AuthStatusContextValue = {
  status: AuthStatus;
  paymentRequired: boolean;
  token: string | null;
  pendingSetupToken: string | null;
};

// 3. Actions context - stable functions that never cause re-renders
type SignInResult = { needsSetup: boolean; requires2FA: boolean; userRole?: UserRole };

type AuthActionsContextValue = {
  signIn: (params: { email: string; password: string }) => Promise<SignInResult>;
  signUp: (params: {
    email: string;
    fullName: string;
    role: "admin" | "teacher" | "parent";
    password: string;
    passwordConfirmation: string;
    parentInviteCode?: string;
    tosAccepted: boolean;
  }) => Promise<void>;
  tenantSignUp: (params: {
    email: string;
    password: string;
    organizationName: string;
    fullName: string;
    contactPhone?: string;
    ownerDob?: string;
    tosAccepted: boolean;
  }) => Promise<void>;
  completeAdminSetup: (params: {
    organizationName: string;
    fullName: string;
    contactPhone?: string;
    tosAccepted: boolean;
  }) => Promise<void>;
  completeParentSetup: (params: {
    fullName: string;
    parentInviteCode: string;
    tosAccepted: boolean;
  }) => Promise<void>;
  clearPendingSetup: () => void;
  signOut: () => Promise<void>;
  signOutAndForgetDevice: () => Promise<void>;
  refreshMe: () => Promise<void>;
  selectCenter: (centerId: number) => void;
};

// Combined type for backward compatibility
type AuthContextValue = AuthUserContextValue &
  AuthStatusContextValue &
  AuthActionsContextValue;

const AuthUserContext = createContext<AuthUserContextValue | null>(null);
const AuthStatusContext = createContext<AuthStatusContextValue | null>(null);
const AuthActionsContext = createContext<AuthActionsContextValue | null>(null);

// Center-scoped API paths that must be invalidated on center switch
const CENTER_SCOPED_PATTERN = /\/api\/v1\/(children|classrooms|activities|attendance|incidents|schedules|time_entries|compliance|dashboard|classroom_enrollments|kiosk)/;

export function AuthProvider(props: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [assignedClassroomIds, setAssignedClassroomIds] = useState<number[]>(
    []
  );
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<MeSubscription | null>(null);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [centers, setCenters] = useState<Center[]>([]);
  const [selectedCenterId, setSelectedCenterIdState] = useState<number | null>(getSelectedCenterId());
  const [defaultCenterId, setDefaultCenterId] = useState<number | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [dataUseAcknowledgment, setDataUseAcknowledgment] = useState<DataUseAcknowledgmentConfig | null>(null);
  const [programType, setProgramType] = useState<string | null>(null);
  const [homeLicenseType, setHomeLicenseType] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingSetupToken, setPendingSetupToken] = useState<string | null>(null);

  // Fetch Rails user data using Supabase token
  // Returns: { success: true } if user found and authenticated
  //          { success: false, needsSetup: true } if user NOT in Rails (needs registration)
  //          { success: false, needsSetup: false, error } for other errors
  type FetchResult =
    | { success: true; role: UserRole }
    | { success: false; needsSetup: true }
    | { success: false; needsSetup: false; paymentRequired: true }
    | { success: false; needsSetup: false; error: string; paymentRequired?: false };

  const fetchRailsUser = useCallback(async (accessToken: string): Promise<FetchResult> => {
    try {
      const res = await apiFetch<MeResponse>("/api/v1/me", {
        token: accessToken,
      });
      setUser(res.data.user);
      setTenantId(res.data.tenant_id);
      setAssignedClassroomIds(res.data.assigned_classroom_ids ?? []);
      setOrganizationName(res.data.organization_name);
      if (res.data.organization_name) setCradleosCookie(res.data.organization_name);
      setSubscription(res.data.subscription);
      setTenantSettings(res.data.tenant_settings ?? null);
      setCenters(res.data.centers ?? []);
      setDefaultCenterId(res.data.default_center_id ?? null);
      setPermissions(res.data.permissions ?? null);
      setDataUseAcknowledgment(res.data.data_use_acknowledgment ?? null);
        setProgramType(res.data.program_type ?? null);
        setHomeLicenseType(res.data.home_license_type ?? null);
      // Auto-select a center: use saved center if valid, otherwise default or first
      const savedCenterId = getSelectedCenterId();
      const availableCenters = res.data.centers ?? [];
      const availableCenterIds = availableCenters.map((c: Center) => c.id);
      const isSavedValid = savedCenterId !== null && availableCenterIds.includes(savedCenterId);
      if (!isSavedValid && availableCenters.length > 0) {
        const autoId =
          res.data.default_center_id && availableCenterIds.includes(res.data.default_center_id)
            ? res.data.default_center_id
            : availableCenters[0].id;
        setSelectedCenterId(autoId);
        setSelectedCenterIdState(autoId);
      }
      setStatus("authenticated");
      return { success: true, role: res.data.user.role };
    } catch (err) {

      // Only treat "User not found" 401 as needing setup
      // Other errors (network, server, etc.) should be surfaced as errors
      if (err instanceof ApiError && err.status === 401) {
        const isUserNotFound = err.errors.some(e =>
          e.toLowerCase().includes("user not found")
        );
        if (isUserNotFound) {
          return { success: false, needsSetup: true };
        }
        // Other 401 errors (invalid token, etc.)
        return { success: false, needsSetup: false, error: err.errors.join(", ") || "Authentication failed" };
      }

      // 402 Payment Required — tenant requires an active subscription
      if (err instanceof ApiError && err.status === 402 && err.errorCategory === "payment_required") {
        return { success: false, needsSetup: false, paymentRequired: true };
      }

      // Network or server errors — prefer user-friendly ApiError messages
      const errorMsg =
        err instanceof ApiError && err.errors.length > 0
          ? err.errors[0]
          : err instanceof Error
            ? err.message
            : "Failed to connect to server";
      return { success: false, needsSetup: false, error: errorMsg };
    }
  }, []);

  // Handle auth state changes from Supabase
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      if (initialSession?.access_token) {
        fetchRailsUser(initialSession.access_token).then((result) => {
          if (!result.success) {
            if (result.needsSetup) {
              setPendingSetupToken(initialSession.access_token);
            } else if ("paymentRequired" in result && result.paymentRequired) {
              setPaymentRequired(true);
              setStatus("payment_required");
              setIsInitialized(true);
              return;
            }
            setStatus("anonymous");
          }
          setIsInitialized(true);
        });
      } else {
        setStatus("anonymous");
        setIsInitialized(true);
      }
    });

    // Listen for auth state changes
    // Use setTimeout(0) to defer state updates to the next event loop iteration
    // This avoids React error #300 ("Cannot update a component while rendering
    // a different component") which happens when onAuthStateChange fires during
    // a React render cycle. queueMicrotask is insufficient because microtasks
    // still run in the same event loop iteration.
    //
    // IMPORTANT: We only handle SIGNED_OUT here. SIGNED_IN is handled by signIn()
    // and TOKEN_REFRESHED by the initial session check. This prevents duplicate
    // fetchRailsUser calls which cause React error #310.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Defer state updates to the next event loop iteration
      setTimeout(() => {
        setSession(newSession);

        // Only handle sign out - sign in is handled by the signIn() function
        if (event === "SIGNED_OUT" || !newSession) {
          setUser(null);
          setTenantId(null);
          setAssignedClassroomIds([]);
          setOrganizationName(null);
          setSubscription(null);
          setTenantSettings(null);
          setCenters([]);
          setDefaultCenterId(null);
          setPermissions(null);
          setDataUseAcknowledgment(null);
          setProgramType(null);
          setHomeLicenseType(null);
          setSelectedCenterIdState(null);
          clearSelectedCenterId();
          setPendingSetupToken(null);
          setPaymentRequired(false);
          setStatus("anonymous");
          clearApiCache();
        }
      }, 0);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchRailsUser]);

  const refreshMe = useCallback(async () => {
    const {
      data: { session: currentSession },
    } = await supabase.auth.getSession();

    if (!currentSession?.access_token) {
      setUser(null);
      setTenantId(null);
      setAssignedClassroomIds([]);
      setOrganizationName(null);
      setSubscription(null);
      setTenantSettings(null);
      setCenters([]);
      setDefaultCenterId(null);
      setPermissions(null);
      setDataUseAcknowledgment(null);
      setProgramType(null);
      setHomeLicenseType(null);
      setStatus("anonymous");
      return;
    }

    await fetchRailsUser(currentSession.access_token);
  }, [fetchRailsUser]);

  const signIn = useCallback(
    async (params: { email: string; password: string }): Promise<SignInResult> => {
      setStatus("loading");

      const { data, error } = await supabase.auth.signInWithPassword({
        email: params.email,
        password: params.password,
      });

      if (error) {
        setStatus("anonymous");
        throw new Error(error.message);
      }

      if (data.session?.access_token) {
        // Set session immediately - don't rely on onAuthStateChange which is deferred
        setSession(data.session);
        const result = await fetchRailsUser(data.session.access_token);

        if (!result.success) {
          if (result.needsSetup) {
            // User exists in Supabase but not Rails - needs to complete setup
            setPendingSetupToken(data.session.access_token);
            setStatus("anonymous");
            return { needsSetup: true, requires2FA: false };
          }
          if ("paymentRequired" in result && result.paymentRequired) {
            // Tenant requires payment — keep the session, show subscribe page
            setPaymentRequired(true);
            setStatus("payment_required");
            return { needsSetup: false, requires2FA: false };
          }
          // Other error (network, invalid token, server error)
          // Sign out of Supabase and throw the error to show on login page
          await supabase.auth.signOut();
          setStatus("anonymous");
          throw new Error(result.error);
        }

        // Check if we have a valid trusted device token
        const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY);
        if (trustedDeviceToken) {
          try {
            const res = await apiFetch<TwoFactorValidateSessionResponse>(
              "/api/v1/auth/2fa/validate-session",
              {
                method: "POST",
                body: { trusted_device_token: trustedDeviceToken },
                token: data.session.access_token,
              }
            );

            if (res.data.valid) {
              // Token is valid, proceed without 2FA
              return { needsSetup: false, requires2FA: false, userRole: result.role };
            }
            // If response says not valid (shouldn't happen with current API), clear and require 2FA
            localStorage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);
          } catch {
            // Token validation failed, clear it and require 2FA
            localStorage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);
          }
        }

        // No valid trusted device token - require 2FA
        return { needsSetup: false, requires2FA: true, userRole: result.role };
      }
      return { needsSetup: false, requires2FA: false };
    },
    [fetchRailsUser]
  );

  const signUp = useCallback(
    async (params: {
      email: string;
      fullName: string;
      role: "admin" | "teacher" | "parent";
      password: string;
      passwordConfirmation: string;
      parentInviteCode?: string;
      tosAccepted: boolean;
    }) => {
      setStatus("loading");

      // 1. Create Supabase Auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: params.email,
        password: params.password,
      });

      if (authError) {
        setStatus("anonymous");
        throw new Error(authError.message);
      }

      if (!authData.session?.access_token) {
        setStatus("anonymous");
        // Supabase might require email confirmation
        throw new Error(
          "Please check your email to confirm your account before signing in."
        );
      }

      // 2. Create Rails user record with Supabase token
      try {
        // Set session immediately - don't rely on onAuthStateChange which is deferred
        setSession(authData.session);
        const res = await apiFetch<MeResponse>("/api/v1/auth/setup", {
          method: "POST",
          body: {
            user: {
              email: params.email,
              full_name: params.fullName,
              role: params.role,
              ...(params.parentInviteCode?.trim()
                ? { parent_invite_code: params.parentInviteCode.trim() }
                : {}),
              tos_accepted: params.tosAccepted,
            },
          },
          token: authData.session.access_token,
        });

        setUser(res.data.user);
        setTenantId(res.data.tenant_id);
        setAssignedClassroomIds(res.data.assigned_classroom_ids ?? []);
        setOrganizationName(res.data.organization_name);
        if (res.data.organization_name) setCradleosCookie(res.data.organization_name);
        setSubscription(res.data.subscription);
        setTenantSettings(res.data.tenant_settings ?? null);
        setCenters(res.data.centers ?? []);
        setDefaultCenterId(res.data.default_center_id ?? null);
        setPermissions(res.data.permissions ?? null);
        setDataUseAcknowledgment(res.data.data_use_acknowledgment ?? null);
        setProgramType(res.data.program_type ?? null);
        setHomeLicenseType(res.data.home_license_type ?? null);
        setStatus("authenticated");
      } catch (err) {
        // Rails setup failed - sign out of Supabase
        await supabase.auth.signOut();
        setStatus("anonymous");
        throw err;
      }
    },
    []
  );

  const tenantSignUp = useCallback(
    async (params: {
      email: string;
      password: string;
      organizationName: string;
      fullName: string;
      contactPhone?: string;
      ownerDob?: string;
      tosAccepted: boolean;
    }) => {
      setStatus("loading");

      // 1. Create Supabase Auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: params.email,
        password: params.password,
      });

      if (authError) {
        setStatus("anonymous");
        throw new Error(authError.message);
      }

      if (!authData.session?.access_token) {
        setStatus("anonymous");
        throw new Error(
          "Please check your email to confirm your account before signing in."
        );
      }

      // 2. Create tenant and admin user in Rails
      try {
        const res = await apiFetch<MeResponse>("/api/v1/auth/tenant_setup", {
          method: "POST",
          body: {
            signup: {
              organization_name: params.organizationName,
              email: params.email,
              full_name: params.fullName,
              contact_phone: params.contactPhone,
              owner_dob: params.ownerDob,
              tos_accepted: params.tosAccepted,
            },
          },
          token: authData.session.access_token,
        });

        setUser(res.data.user);
        setTenantId(res.data.tenant_id);
        setAssignedClassroomIds(res.data.assigned_classroom_ids ?? []);
        setOrganizationName(res.data.organization_name);
        if (res.data.organization_name) setCradleosCookie(res.data.organization_name);
        setSubscription(res.data.subscription);
        setTenantSettings(res.data.tenant_settings ?? null);
        setCenters(res.data.centers ?? []);
        setDefaultCenterId(res.data.default_center_id ?? null);
        setPermissions(res.data.permissions ?? null);
        setDataUseAcknowledgment(res.data.data_use_acknowledgment ?? null);
        setProgramType(res.data.program_type ?? null);
        setHomeLicenseType(res.data.home_license_type ?? null);
        setStatus("authenticated");
      } catch (err) {
        // Rails setup failed - sign out of Supabase
        await supabase.auth.signOut();
        setStatus("anonymous");
        throw err;
      }
    },
    []
  );

  const forceLocalSignOut = useCallback(() => {
    // Nuclear option: manually reset all auth state when Supabase signOut fails.
    // This handles Safari where the global signOut API call or lock acquisition
    // can fail silently, leaving the user stuck in an authenticated state.
    clearCradleosCookie();
    setSession(null);
    setUser(null);
    setTenantId(null);
    setAssignedClassroomIds([]);
    setOrganizationName(null);
    setSubscription(null);
    setTenantSettings(null);
    setCenters([]);
    setDefaultCenterId(null);
    setPermissions(null);
    setDataUseAcknowledgment(null);
    setSelectedCenterIdState(null);
    clearSelectedCenterId();
    setPendingSetupToken(null);
    setPaymentRequired(false);
    setStatus("anonymous");
    clearApiCache();
  }, []);

  const selectCenter = useCallback((centerId: number) => {
    if (centerId === selectedCenterId) return; // no-op for same center
    setSelectedCenterId(centerId);
    setSelectedCenterIdState(centerId);
    startCenterSwitch();
    invalidateCache(CENTER_SCOPED_PATTERN); // Only clear center-scoped data
  }, [selectedCenterId]);

  const signOut = useCallback(async () => {
    // Clear cross-domain cookie before sign out
    clearCradleosCookie();
    // Clear caches before sign out
    clearApiCache();
    clearWelcomeState();
    clearSelectedCenterId();
    // NOTE: We intentionally do NOT clear the 2FA trusted device token on signout.
    // The token remembers this device for 30 days, allowing the user to skip 2FA
    // when signing back in. The token is still secure because:
    // 1. It's tied to a specific user (validated on login)
    // 2. It expires after 30 days
    // 3. It's cryptographically signed by the server

    // Clear pending setup token (not handled by onAuthStateChange)
    setPendingSetupToken(null);

    // Sign out from Supabase - onAuthStateChange listener will handle state updates
    // to avoid race condition / React error #300
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // Global sign out failed (e.g. network/CORS issue on Safari).
        // Fall back to local-only sign out which skips the server call.
        const local = await supabase.auth.signOut({ scope: "local" });
        if (local.error) {
          forceLocalSignOut();
        }
      }
    } catch {
      // signOut can throw if lock acquisition fails (Safari Web Locks issue)
      forceLocalSignOut();
    }
  }, [forceLocalSignOut]);

  // Sign out AND clear the trusted device token (forget this device)
  const signOutAndForgetDevice = useCallback(async () => {
    // Clear cross-domain cookie before sign out
    clearCradleosCookie();
    // Clear caches before sign out
    clearApiCache();
    clearWelcomeState();
    clearSelectedCenterId();
    // Clear the 2FA trusted device token - user will need to verify 2FA on next login
    localStorage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);

    // Clear pending setup token (not handled by onAuthStateChange)
    setPendingSetupToken(null);

    // Sign out from Supabase - onAuthStateChange listener will handle state updates
    // to avoid race condition / React error #300
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        const local = await supabase.auth.signOut({ scope: "local" });
        if (local.error) {
          forceLocalSignOut();
        }
      }
    } catch {
      forceLocalSignOut();
    }
  }, [forceLocalSignOut]);

  const completeAdminSetup = useCallback(
    async (params: {
      organizationName: string;
      fullName: string;
      contactPhone?: string;
      tosAccepted: boolean;
    }) => {
      if (!pendingSetupToken) {
        throw new Error("No pending setup token. Please sign in again.");
      }

      setStatus("loading");

      try {
        const res = await apiFetch<MeResponse>("/api/v1/auth/tenant_setup", {
          method: "POST",
          body: {
            signup: {
              organization_name: params.organizationName,
              full_name: params.fullName,
              contact_phone: params.contactPhone,
              tos_accepted: params.tosAccepted,
            },
          },
          token: pendingSetupToken,
        });

        setUser(res.data.user);
        setTenantId(res.data.tenant_id);
        setAssignedClassroomIds(res.data.assigned_classroom_ids ?? []);
        setOrganizationName(res.data.organization_name);
        if (res.data.organization_name) setCradleosCookie(res.data.organization_name);
        setSubscription(res.data.subscription);
        setTenantSettings(res.data.tenant_settings ?? null);
        setCenters(res.data.centers ?? []);
        setDefaultCenterId(res.data.default_center_id ?? null);
        setPermissions(res.data.permissions ?? null);
        setDataUseAcknowledgment(res.data.data_use_acknowledgment ?? null);
        setProgramType(res.data.program_type ?? null);
        setHomeLicenseType(res.data.home_license_type ?? null);
        setPendingSetupToken(null);
        setStatus("authenticated");
      } catch (err) {
        setStatus("anonymous");
        throw err;
      }
    },
    [pendingSetupToken]
  );

  const completeParentSetup = useCallback(
    async (params: { fullName: string; parentInviteCode: string; tosAccepted: boolean }) => {
      if (!pendingSetupToken) {
        throw new Error("No pending setup token. Please sign in again.");
      }

      setStatus("loading");

      try {
        const res = await apiFetch<MeResponse>("/api/v1/auth/setup", {
          method: "POST",
          body: {
            user: {
              full_name: params.fullName,
              role: "parent",
              parent_invite_code: params.parentInviteCode.trim(),
              tos_accepted: params.tosAccepted,
            },
          },
          token: pendingSetupToken,
        });

        setUser(res.data.user);
        setTenantId(res.data.tenant_id);
        setAssignedClassroomIds(res.data.assigned_classroom_ids ?? []);
        setOrganizationName(res.data.organization_name);
        if (res.data.organization_name) setCradleosCookie(res.data.organization_name);
        setSubscription(res.data.subscription);
        setTenantSettings(res.data.tenant_settings ?? null);
        setCenters(res.data.centers ?? []);
        setDefaultCenterId(res.data.default_center_id ?? null);
        setPermissions(res.data.permissions ?? null);
        setDataUseAcknowledgment(res.data.data_use_acknowledgment ?? null);
        setProgramType(res.data.program_type ?? null);
        setHomeLicenseType(res.data.home_license_type ?? null);
        setPendingSetupToken(null);
        setStatus("authenticated");
      } catch (err) {
        setStatus("anonymous");
        throw err;
      }
    },
    [pendingSetupToken]
  );

  const clearPendingSetup = useCallback(() => {
    setPendingSetupToken(null);
  }, []);

  // Memoize each context value separately to prevent unnecessary re-renders
  const userValue = useMemo<AuthUserContextValue>(
    () => ({ user, tenantId, assignedClassroomIds, organizationName, subscription, tenantSettings, centers, selectedCenterId, defaultCenterId, permissions, dataUseAcknowledgment, programType, homeLicenseType }),
    [user, tenantId, assignedClassroomIds, organizationName, subscription, tenantSettings, centers, selectedCenterId, defaultCenterId, permissions, dataUseAcknowledgment, programType, homeLicenseType]
  );

  const statusValue = useMemo<AuthStatusContextValue>(
    () => ({ status, paymentRequired, token: session?.access_token ?? null, pendingSetupToken }),
    [status, paymentRequired, session?.access_token, pendingSetupToken]
  );

  // Use ref to keep actions stable - they never need to trigger re-renders
  const actionsRef = useRef<AuthActionsContextValue>({
    signIn,
    signUp,
    tenantSignUp,
    completeAdminSetup,
    completeParentSetup,
    clearPendingSetup,
    signOut,
    signOutAndForgetDevice,
    refreshMe,
    selectCenter,
  });
  actionsRef.current = { signIn, signUp, tenantSignUp, completeAdminSetup, completeParentSetup, clearPendingSetup, signOut, signOutAndForgetDevice, refreshMe, selectCenter };

  const actionsValue = useMemo<AuthActionsContextValue>(
    () => ({
      signIn: (...args) => actionsRef.current.signIn(...args),
      signUp: (...args) => actionsRef.current.signUp(...args),
      tenantSignUp: (...args) => actionsRef.current.tenantSignUp(...args),
      completeAdminSetup: (...args) => actionsRef.current.completeAdminSetup(...args),
      completeParentSetup: (...args) => actionsRef.current.completeParentSetup(...args),
      clearPendingSetup: () => actionsRef.current.clearPendingSetup(),
      signOut: (...args) => actionsRef.current.signOut(...args),
      signOutAndForgetDevice: (...args) => actionsRef.current.signOutAndForgetDevice(...args),
      refreshMe: (...args) => actionsRef.current.refreshMe(...args),
      selectCenter: (...args) => actionsRef.current.selectCenter(...args),
    }),
    []
  );

  // Show loading animation until we've checked the initial session
  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <AuthActionsContext.Provider value={actionsValue}>
      <AuthStatusContext.Provider value={statusValue}>
        <AuthUserContext.Provider value={userValue}>
          {props.children}
        </AuthUserContext.Provider>
      </AuthStatusContext.Provider>
    </AuthActionsContext.Provider>
  );
}

// Hooks for accessing specific parts of auth context
// Use these for better performance when you only need specific data

export function useAuthUser(): AuthUserContextValue {
  const ctx = useContext(AuthUserContext);
  if (!ctx) throw new Error("useAuthUser must be used within AuthProvider");
  return ctx;
}

export function useAuthStatus(): AuthStatusContextValue {
  const ctx = useContext(AuthStatusContext);
  if (!ctx) throw new Error("useAuthStatus must be used within AuthProvider");
  return ctx;
}

export function useAuthActions(): AuthActionsContextValue {
  const ctx = useContext(AuthActionsContext);
  if (!ctx) throw new Error("useAuthActions must be used within AuthProvider");
  return ctx;
}

/** Returns the selected center's name (multi-center) or organizationName (single-center). */
export function useCenterDisplayName(): string | null {
  const { centers, selectedCenterId, organizationName } = useAuthUser();
  const selected = centers.find((c) => c.id === selectedCenterId);
  return centers.length > 1 && selected ? selected.name : organizationName;
}

// Combined hook for backward compatibility
// Components using this will re-render on any auth change
export function useAuth(): AuthContextValue {
  const user = useAuthUser();
  const status = useAuthStatus();
  const actions = useAuthActions();

  return useMemo(
    () => ({ ...user, ...status, ...actions }),
    [user, status, actions]
  );
}
